import {
  ConditionalTrialRouting,
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import {mappers} from "../../utils/attributeMapping/mappers";
import {
  CANCER_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,                                                         // 25483, 15527, 25471, 3052, 15039                                                      // 25483, 15527, 25471, 3052, 15039                                                       // 25483, 15527, 25471, 3052, 15039  
  INFECTIOUS_DISEASE_ATTRIBUTES,                                                         // 15025, 23426, 26614, 21796, 23358 
  LIVER_KIDNEY_ATTRIBUTES,                                                         // 21026, 794, 72, 5785, 20254
  MEASUREMENT_ATTRIBUTES,
  MENTAL_HEALTH_ATTRIBUTES,                                                         // 499, 3052, 15039 
  NEUROLOGICAL_ATTRIBUTES,
  RESPIRATORY_ATTRIBUTES,                                                         // 25661, 25767                 
  VACCINE_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for COVID-19 Vaccine Studies - Healthy Adults 50-64
 * Trial Bucket ID: VVACCOV1125
 * Target population: Very healthy adults 50-64 (BMI <40, no major chronic conditions)
 *
 * This prescreener captures participants for COVID-19 vaccine studies
 * evaluating investigational vaccines in adults aged 50-64 without underlying
 * high-risk conditions.
 */
export const covid50to64HealthyScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "under_50",
        label: "Under 50",
        disqualify: true
      },
      {
        value: "50_to_64",
        label: "50 to 64"
      },
      {
        value: "65_or_older",
        label: "65 or older",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study is for adults aged 50 to 64.",
    attributeMapping: mappers.value({
      under_50: [],
      "50_to_64": [],
      "65_or_older": [],
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    maxBMI: 40,
    disqualifyMessage: "This study requires a BMI less than 40 kg/m².",
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },

  {
    id: "hypertension",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with high blood pressure (hypertension)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I have not been diagnosed with high blood pressure"
      },
      {
        value: "yes",
        label: "Yes, I have been diagnosed with high blood pressure",
        // Conditional disqualification: only disqualify if BMI > 30
        disqualify: (allAnswers) => {
          const bmi = allAnswers.bmi as number | undefined;
          console.log('🔍 Hypertension disqualify check:', { bmi, allAnswers });
          // If BMI is 30 or less, don't disqualify even if they have hypertension
          if (bmi !== undefined && bmi <= 30) {
            console.log('✅ BMI <= 30, allowing hypertension');
            return false;
          }
          // If BMI > 30 (or not calculated yet), disqualify if they have hypertension
          console.log('❌ BMI > 30 or undefined, disqualifying for hypertension');
          return true;
        },
      },
    ],
    disqualifyMessage: "This study cannot include participants with high blood pressure and a BMI over 30.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "mood_disorder",
    type: QuestionType.CHECKBOX,
    question: "Has a doctor diagnosed you with any of the following conditions?",
    helpText: "Select all that apply. If none apply or you're unsure, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "current_depression_symptoms",
        label: "Diagnosed with depression and experiencing symptoms (such as low mood, tiredness, changes in appetite, or loss of interest in activities)",
        disqualify: true
      },
      {
        value: "bipolar_disorder",
        label: "Diagnosed with bipolar disorder",
        disqualify: true
      },
      {
        value: "schizophrenia",
        label: "Diagnosed with schizophrenia",
        disqualify: true
      },
      {
        value: "schizoaffective_disorder",
        label: "Diagnosed with schizoaffective disorder",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "This study cannot include participants with certain mood or mental health conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        current_depression_symptoms: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION, booleanAttributeValue: true },
        ],
        bipolar_disorder: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_BIPOLAR, booleanAttributeValue: true },
        ],
        schizophrenia: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SCHIZOPHRENIA, booleanAttributeValue: true },
        ],
        schizoaffective_disorder: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SCHIZOAFFECTIVE, booleanAttributeValue: true },
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION, booleanAttributeValue: false },
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_BIPOLAR, booleanAttributeValue: false },
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SCHIZOPHRENIA, booleanAttributeValue: false },
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SCHIZOAFFECTIVE, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "recent_covid_infection",
    type: QuestionType.RADIO,
    question: "In the past 3 months, have you had a positive COVID-19 test (PCR or antigen)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true
      },
      {
        value: "no",
        label: "No"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have had a COVID-19 infection in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 24406, booleanAttributeValue: true }, // had_covid_6_months
      ],
      no: [
        { attributeDefinitionId: 24406, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_covid_vaccine",
    type: QuestionType.RADIO,
    question: "In the past 3 months, have you received any COVID-19 vaccine?",
    helpText: "This includes any COVID-19 vaccine such as Moderna, Pfizer, or Novavax.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true
      },
      {
        value: "no",
        label: "No",
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received a COVID-19 vaccine in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: VACCINE_ATTRIBUTES.HAD_COVID_VACCINE_6_MONTHS, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: VACCINE_ATTRIBUTES.HAD_COVID_VACCINE_6_MONTHS, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "diabetes",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with diabetes (Type 1 or Type 2)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I do not have diabetes"
      },
      {
        value: "type1",
        label: "Yes, I have Type 1 diabetes",
        disqualify: true
      },
      {
        value: "type2",
        label: "Yes, I have Type 2 diabetes",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study is for adults without diabetes.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES, booleanAttributeValue: false },
      ],
      type1: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true },
      ],
      type2: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "cardiovascular_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you experienced or been diagnosed with any of the following cardiovascular (heart/blood vessel) conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "stroke_cva",
        label: "Stroke, mini-stroke, or a transient ischemic attack (TIA)",
        disqualify: true
      },
      {
        value: "heart_attack",
        label: "Heart attack (myocardial infarction)",
        disqualify: true
      },
      {
        value: "heart_failure",
        label: "Heart failure (congestive heart failure)",
        disqualify: true
      },
      {
        value: "coronary_artery_disease",
        label: "Coronary artery disease (heart disease)",
        disqualify: true
      },
      {
        value: "heart_inflammation_recent",
        label: "Myocarditis (heart muscle inflammation) or pericarditis (heart lining inflammation) in the past 3 months",
        disqualify: true
      },
      {
        value: "cardiomyopathy",
        label: "Cardiomyopathy (heart muscle disease)",
        disqualify: true
      },
      {
        value: "pulmonary_hypertension",
        label: "Pulmonary hypertension (high blood pressure in the lungs)",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "This study cannot include participants with cardiovascular conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        stroke_cva: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
        ],
        heart_attack: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        ],
        heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
        coronary_artery_disease: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: true },
        ],
        heart_inflammation_recent: [
          { attributeDefinitionId: 25888, booleanAttributeValue: true }, // history_myocarditis_pericarditis_prerequisite
        ],
        cardiomyopathy: [
          { attributeDefinitionId: 23518, booleanAttributeValue: true }, // has_cardiomyopathy
        ],
        pulmonary_hypertension: [
          { attributeDefinitionId: 8249, booleanAttributeValue: true }, // has_pulmonaryhypertension
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: false },
          { attributeDefinitionId: 25888, booleanAttributeValue: false },
          { attributeDefinitionId: 23518, booleanAttributeValue: false },
          { attributeDefinitionId: 8249, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "asthma",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with asthma?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I do not have asthma"
      },
      {
        value: "yes_mild",
        label: "Yes, I have mild asthma (occasional symptoms, no daily medication needed)"
      },
      {
        value: "yes_moderate_severe",
        label: "Yes, I take medication daily for my asthma (such as inhaled corticosteroids or combination inhalers)",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study cannot include participants with moderate to severe asthma requiring daily preventer medication.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: false },
      ],
      yes_mild: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true },
      ],
      yes_moderate_severe: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "chronic_lung_disease",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following chronic lung conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "copd",
        label: "COPD (chronic obstructive pulmonary disease) or chronic bronchitis",
        disqualify: true
      },
      {
        value: "interstitial_lung_disease",
        label: "Lung fibrosis or interstitial lung disease (ILD)",
        disqualify: true
      },
      {
        value: "pulmonary_embolism",
        label: "History of pulmonary embolism (blood clot in the lungs)",
        disqualify: true
      },
      {
        value: "cystic_fibrosis",
        label: "Cystic fibrosis",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "This study cannot include participants with chronic lung disease.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        copd: [
          { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: true },
        ],
        interstitial_lung_disease: [
          { attributeDefinitionId: 25661, booleanAttributeValue: true }, // has_ILD_prerequisite
        ],
        pulmonary_embolism: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_BLOOD_CLOT, booleanAttributeValue: true },
        ],
        cystic_fibrosis: [
          { attributeDefinitionId: 25767, booleanAttributeValue: true }, // has_CF_prerequisite
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: false },
          { attributeDefinitionId: 25661, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_BLOOD_CLOT, booleanAttributeValue: false },
          { attributeDefinitionId: 25767, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "immunocompromised_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of these apply to you?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "organ_transplant",
        label: "I have a history of organ transplant or I am currently on the transplant waitlist",
        disqualify: true
      },
      {
        value: "immunosuppressive_medications",
        label: "I take immunosuppressing medications (such as prednisone, methotrexate, azathioprine, cyclosporine, mycophenolate, or rituximab)",
        disqualify: true
      },
      {
        value: "active_tb",
        label: "I have active tuberculosis (TB) or I am currently on TB treatment",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants who are immunocompromised or taking immunosuppressive medications.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        organ_transplant: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
        ],
        immunosuppressive_medications: [
          { attributeDefinitionId: 25884, booleanAttributeValue: true }, // is_immunocompromised_prerequisite
        ],
        active_tb: [
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: true },
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
          { attributeDefinitionId: 25884, booleanAttributeValue: false },
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "hiv_status",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with HIV?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I have not been diagnosed with HIV"
      },
      {
        value: "controlled",
        label: "Yes, my HIV is well-controlled (stable on antiretroviral medication with undetectable viral load)",
        // Only disqualify if BMI >= 30 (no Novavax trials available, only Moderna which excludes HIV)
        disqualify: (allAnswers) => {
          const bmi = allAnswers.bmi as number | undefined;
          // Conservative default: if BMI unknown, disqualify (shouldn't happen due to question order)
          if (bmi === undefined) {
            return true;
          }
          // BMI < 30: Novavax trials available, don't disqualify
          // BMI >= 30: Only Moderna trials, which don't allow HIV
          return bmi >= 30;
        },
      },
      {
        value: "uncontrolled",
        label: "Yes, my HIV is not well-controlled or I am not currently on treatment",
        disqualify: true
      },
    ],
    disqualifyMessage: "Based on your answers, you are not eligible for any of the studies we are currently recruiting for.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: false },
      ],
      controlled: [
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
        { attributeDefinitionId: 23426, booleanAttributeValue: false }, // has_HIV_advanced (false = controlled)
      ],
      uncontrolled: [
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
        { attributeDefinitionId: 23426, booleanAttributeValue: true }, // has_HIV_advanced (true = uncontrolled)
      ],
    }),
  },

  {
    id: "neurological_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "dementia",
        label: "Dementia (formally diagnosed)",
        disqualify: true
      },
      {
        value: "parkinsons",
        label: "Parkinson's disease",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "This study cannot include participants with certain neurological conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        dementia: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEMENTIA, booleanAttributeValue: true },
        ],
        parkinsons: [
          { attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_PARKINSONS, booleanAttributeValue: true },
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEMENTIA, booleanAttributeValue: false },
          { attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_PARKINSONS, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "adhd_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed by a healthcare provider with ADHD (attention-deficit/hyperactivity disorder)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I have not been diagnosed with ADHD"
      },
      {
        value: "yes",
        label: "Yes, I have been diagnosed with ADHD",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study cannot include participants with a diagnosis of ADHD.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_ADHD, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_ADHD, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "smoking_status",
    type: QuestionType.RADIO,
    question: "Do you currently smoke cigarettes, e-cigarettes or vapes, or have you regularly smoked cigarettes, e-cigarettes or vapes in the past?",
    helpText: "\"Regularly\" means smoking most days or as part of a routine.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I have never regularly smoked cigarettes, e-cigarettes or vapes"
      },
      {
        value: "quit_over_6_months",
        label: "I quit smoking cigarettes, e-cigarettes or vapes more than 6 months ago",
        // Only disqualify if BMI >= 30 (no Novavax trials available, only Moderna which excludes past smokers)
        disqualify: (allAnswers) => {
          const bmi = allAnswers.bmi as number | undefined;
          // Conservative default: if BMI unknown, disqualify (shouldn't happen due to question order)
          if (bmi === undefined) {
            return true;
          }
          // BMI < 30: Novavax trials available, don't disqualify
          // BMI >= 30: Only Moderna trials, which don't allow past smokers
          return bmi >= 30;
        },
      },
      {
        value: "quit_within_6_months",
        label: "I quit smoking cigarettes, e-cigarettes or vapes within the past 6 months",
        disqualify: true
      },
      {
        value: "yes_regularly",
        label: "Yes, I currently smoke cigarettes, e-cigarettes or vapes",
        disqualify: true
      },
    ],
    disqualifyMessage: "Based on your answers, you are not eligible for any of the studies we are currently recruiting for. Current smokers and those who quit within the past 6 months cannot participate.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: false }, // is_pastsmoker
      ],
      quit_over_6_months: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true }, // is_pastsmoker
        { attributeDefinitionId: 23791, booleanAttributeValue: true }, // quit_smoking_6_months
      ],
      quit_within_6_months: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true }, // is_pastsmoker
        { attributeDefinitionId: 23791, booleanAttributeValue: false }, // quit_smoking_6_months
      ],
      yes_regularly: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "other_exclusionary_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "cancer_recent",
        label: "Cancer within the past 5 years (excluding non-melanoma skin cancers such as basal cell or squamous cell carcinoma)",
        disqualify: true
      },
      {
        value: "guillain_barre",
        label: "Guillain-Barré syndrome",
        disqualify: true
      },
      {
        value: "chronic_kidney_disease",
        label: "Chronic kidney disease (CKD)",
        disqualify: true
      },
      {
        value: "liver_disease",
        label: "Liver failure, cirrhosis, fatty liver disease, or NASH",
        disqualify: true
      },
      {
        value: "hepatitis",
        label: "Hepatitis (B or C)",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        cancer_recent: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        guillain_barre: [
          { attributeDefinitionId: 25742, booleanAttributeValue: true }, // had_guillianbarre_prerequisite
        ],
        chronic_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        liver_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        ],
        hepatitis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: 25742, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: false },
        ],
      },
    }),
  },
  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently participating in another clinical trial"
      },
      {
        value: "yes",
        label: "Yes, I am currently participating in another clinical trial",
        disqualify: true
      },
    ],
    disqualifyMessage: "For safety reasons, participants currently enrolled in another clinical trial cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

];

/**
 * Conditional trial routing for COVID-19 vaccine studies
 *
 * Trial constraints:
 * - VMODCOVP401 (Velocity Moderna): BMI < 40, excludes hypertension, excludes past smokers, excludes HIV
 * - TMODCOVP401 (Tekton Moderna): BMI ≥ 30, excludes hypertension, excludes past smokers, excludes HIV
 * - VCOVI91081 (Velocity Pfizer): BMI < 30, includes hypertension, excludes past smokers, excludes HIV
 * - TCOVI91081 (Tekton Pfizer): BMI < 30, includes hypertension, excludes past smokers, excludes HIV
 * - VCOVIV420 (Velocity Novavax): BMI < 30, includes hypertension, allows past smokers who quit 6+ months ago, allows controlled HIV
 * - M3COVIV420 (M3 Novavax): BMI < 30, includes hypertension, allows past smokers who quit 6+ months ago, allows controlled HIV
 * - M3COVI91081 (M3 Pfizer): BMI < 30, includes hypertension, excludes past smokers, excludes HIV
 *
 * VCOVI91081 (Velocity Pfizer) is the highest priority trial where eligible (BMI < 30).
 *
 * Routing logic:
 * - BMI ≥ 30 + no hypertension: Moderna (Velocity then Tekton) - past smokers and HIV excluded
 * - BMI < 30 + hypertension: Velocity Pfizer → Tekton Pfizer → Novavax (Velocity then M3) - past smokers/controlled HIV get Novavax only
 * - BMI < 30 + no hypertension: Velocity Pfizer → Velocity Moderna → Tekton Pfizer → Novavax (Velocity then M3) → Tekton Moderna - past smokers/controlled HIV get Novavax only
 */
export const covidVaccineTrialRouting: ConditionalTrialRouting = {
  getEligibleTrials: (answers, age) => {
    if (age && (age > 64 || age < 50)) {
      return [];
    }

    // Get BMI from answers (calculated by PreScreener component)
    const bmi = answers.bmi as number | undefined;
    const hasHypertension = answers.hypertension === "yes";
    const smokingStatus = answers.smoking_status as string | undefined;
    const hivStatus = answers.hiv_status as string | undefined;
    const isPastSmokerQuit6Mo = smokingStatus === "quit_over_6_months";
    const hasControlledHIV = hivStatus === "controlled";

    // Novavax-only conditions: past smoker who quit 6+ mo OR controlled HIV
    const novavaxOnly = isPastSmokerQuit6Mo || hasControlledHIV;

    if (bmi === undefined) {
      return [];
    }

    // BMI ≥ 30 + no hypertension: Moderna only
    if (bmi >= 30 && !hasHypertension) {
      // Past smokers and controlled HIV cannot do Moderna
      if (novavaxOnly) {
        return [];
      }
      return ["VMODCOVP401", "TMODCOVP401"];
    }

    // BMI < 30 + hypertension: Pfizer → Novavax
    if (bmi < 30 && hasHypertension) {
      if (novavaxOnly) {
        return ["VCOVIV420", "M3COVIV420"]; // Novavax only
      }
      return ["VCOVI91081", "TCOVI91081", "VCOVIV420", "M3COVIV420", "M3COVI91081"];
    }

    // BMI < 30 + no hypertension: Try all 7, priority order
    if (bmi < 30 && !hasHypertension) {
      if (novavaxOnly) {
        return ["VCOVIV420", "M3COVIV420"]; // Novavax only
      }
      return ["VCOVI91081", "VMODCOVP401", "TCOVI91081", "VCOVIV420", "M3COVIV420", "M3COVI91081", "TMODCOVP401"];
    }

    return [];
  },
};
